import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import LocationSelect from './LocationSelect';
import JobLogViewer from './JobLogViewer';
import './ZeroLocationPage.css';

export default function ZeroLocationPage() {
  const [locations,    setLocations]    = useState([]);
  const [locsLoading,  setLocsLoading]  = useState(true);
  const [locsError,    setLocsError]    = useState(null);
  const [selectedLocs, setSelectedLocs] = useState([]);

  const [preview,        setPreview]        = useState(null);
  const [confirmText,    setConfirmText]    = useState('');
  const [result,         setResult]         = useState(null);
  const [error,          setError]          = useState(null);

  const [backups, setBackups] = useState([]);

  // ── active job (preview or execute — both run as background jobs since
  // fetching/zeroing many locations is a long serial chain of Shopify calls) ──
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobType,      setJobType]      = useState(null); // 'preview' | 'execute'
  const [jobLogs,      setJobLogs]      = useState([]);
  const [jobStatus,    setJobStatus]    = useState(null);
  const [logsOpen,     setLogsOpen]     = useState(true);
  const logViewerRef = useRef();

  function loadLocations() {
    setLocsLoading(true);
    setLocsError(null);
    axios.get('/api/zero-location/locations')
      .then(r => { setLocations(r.data); setLocsLoading(false); })
      .catch(e => { setLocsError(e.response?.data?.error || e.message); setLocsLoading(false); });
  }

  function loadBackups() {
    axios.get('/api/zero-location/backups').then(r => setBackups(r.data)).catch(() => {});
  }

  useEffect(() => { loadLocations(); loadBackups(); }, []);

  // ── poll job logs while a preview or execute job is running ─────────────────
  useEffect(() => {
    if (!currentJobId || jobStatus === 'complete' || jobStatus === 'error') return;
    const iv = setInterval(() => {
      axios.get(`/api/zero-location/logs/${currentJobId}`)
        .then(r => {
          setJobLogs(r.data.lines);
          setJobStatus(r.data.status);
          if (r.data.status === 'complete') {
            clearInterval(iv);
            if (jobType === 'preview') {
              setPreview(r.data.result);
              setCurrentJobId(null);
              setJobStatus(null);
              setJobType(null);
            } else {
              setResult(r.data.result);
              loadBackups();
            }
          } else if (r.data.status === 'error') {
            clearInterval(iv);
            setError(r.data.error);
            if (jobType === 'preview') {
              setCurrentJobId(null);
              setJobStatus(null);
              setJobType(null);
            }
          }
        })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(iv);
  }, [currentJobId, jobStatus, jobType]);

  // auto-scroll log viewer — only when already near the bottom
  useEffect(() => {
    if (!logsOpen || !logViewerRef.current) return;
    const el = logViewerRef.current;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [jobLogs, logsOpen]);

  function selectLocations(locs) {
    setSelectedLocs(locs);
    setPreview(null);
    setConfirmText('');
    setResult(null);
    setError(null);
  }

  async function handlePreview() {
    if (selectedLocs.length === 0) return;
    setError(null);
    setResult(null);
    try {
      const r = await axios.post('/api/zero-location/preview', {
        location_ids: selectedLocs.map(l => l.id),
      });
      setCurrentJobId(r.data.job_id);
      setJobType('preview');
      setJobLogs([]);
      setJobStatus('pending');
      setLogsOpen(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }

  const expectedConfirm = selectedLocs.map(l => l.name).join(', ');

  async function handleExecute() {
    if (selectedLocs.length === 0 || confirmText !== expectedConfirm) return;
    setError(null);
    try {
      const r = await axios.post('/api/zero-location/execute', {
        location_ids: selectedLocs.map(l => l.id),
        confirm_name: confirmText,
      });
      setCurrentJobId(r.data.job_id);
      setJobType('execute');
      setJobLogs([]);
      setJobStatus('pending');
      setLogsOpen(true);
      setPreview(null);
      setConfirmText('');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }

  function reset() {
    setSelectedLocs([]);
    setPreview(null);
    setConfirmText('');
    setResult(null);
    setError(null);
    setCurrentJobId(null);
    setJobType(null);
    setJobLogs([]);
    setJobStatus(null);
  }

  const confirmMatches = selectedLocs.length > 0 && confirmText === expectedConfirm;
  const jobRunning     = currentJobId && jobStatus !== 'complete' && jobStatus !== 'error';

  return (
    <div className="zl-page">
      <h1>Zero Out Location</h1>
      <p className="zl-sub">
        Set every SKU's on-hand inventory to zero at a Shopify location. A full backup of
        current quantities is written and downloadable <strong>before</strong> anything is changed,
        so it can be re-imported through Shopify's own inventory import if needed.
      </p>

      <div className="zl-banner zl-banner--info">
        Shipping Pilot can never be zeroed out through this tool and won't appear in the list below.
      </div>

      {locsError && (
        <div className="zl-banner zl-banner--warn">Could not load Shopify locations: {locsError}</div>
      )}
      {locsLoading && locations.length === 0 && !locsError && (
        <div className="zl-banner zl-banner--info">Loading locations…</div>
      )}

      {currentJobId && (
        <JobLogViewer
          title={jobType === 'preview' ? 'Preview Job' : 'Zero Out Job'}
          jobId={currentJobId}
          status={jobStatus}
          logs={jobLogs}
          open={logsOpen}
          onToggleOpen={() => setLogsOpen(o => !o)}
          onDismiss={(jobStatus === 'complete' || jobStatus === 'error') ? reset : null}
          logViewerRef={logViewerRef}
        />
      )}

      {!currentJobId && !result && (
        <>
          <div className="zl-field">
            <label className="zl-label">Locations</label>
            <LocationSelect locations={locations} value={selectedLocs} onChange={selectLocations} multiple />
          </div>

          {selectedLocs.length > 0 && !preview && (
            <div className="zl-actions">
              <button className="zl-btn-primary" onClick={handlePreview}>
                {`Preview Inventory (${selectedLocs.length} location${selectedLocs.length !== 1 ? 's' : ''})`}
              </button>
            </div>
          )}

          {preview && (
            <div className="zl-preview">
              <div className="zl-preview-summary">
                {preview.locations.length} location{preview.locations.length !== 1 ? 's' : ''} — {preview.sku_count} SKU{preview.sku_count !== 1 ? 's' : ''} with stock,{' '}
                {preview.total_units} total unit{preview.total_units !== 1 ? 's' : ''}
              </div>

              {preview.sku_count === 0 ? (
                <div className="zl-banner zl-banner--info" style={{ marginTop: 12 }}>
                  These locations already have no on-hand inventory. Nothing to do.
                </div>
              ) : (
                <>
                  {preview.locations.map(loc => (
                    <div key={loc.location_id} className="zl-preview-loc">
                      <div className="zl-preview-loc-title">
                        {loc.location_name} — {loc.sku_count} SKU{loc.sku_count !== 1 ? 's' : ''}, {loc.total_units} unit{loc.total_units !== 1 ? 's' : ''}
                      </div>
                      {loc.sku_count > 0 && (
                        <div className="zl-preview-table-wrap">
                          <table className="zl-preview-table">
                            <thead>
                              <tr><th>SKU</th><th>Title</th><th>Available</th></tr>
                            </thead>
                            <tbody>
                              {loc.rows.map(row => (
                                <tr key={row.sku}>
                                  <td>{row.sku}</td>
                                  <td>{row.title}</td>
                                  <td>{row.available}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="zl-danger-zone">
                    <div className="zl-banner zl-banner--danger">
                      This will set all {preview.sku_count} SKUs above to 0 across {preview.locations.length} location{preview.locations.length !== 1 ? 's' : ''}.
                      A backup CSV per location is saved automatically first.
                    </div>
                    <label className="zl-label">
                      Type <strong>{expectedConfirm}</strong> to confirm
                    </label>
                    <input
                      className="zl-confirm-input"
                      value={confirmText}
                      onChange={e => setConfirmText(e.target.value)}
                      placeholder={expectedConfirm}
                      autoComplete="off"
                    />
                    <div className="zl-actions">
                      <button className="zl-btn-secondary" onClick={reset}>Cancel</button>
                      <button
                        className="zl-btn-danger"
                        onClick={handleExecute}
                        disabled={!confirmMatches}
                      >
                        {`Download Backups & Zero Out ${preview.locations.length} Location${preview.locations.length !== 1 ? 's' : ''}`}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {!jobRunning && result && (
        <div className="zl-result">
          <div className="zl-banner zl-banner--success">
            Zeroed {result.zeroed_count} SKU{result.zeroed_count !== 1 ? 's' : ''}
            ({result.total_units} unit{result.total_units !== 1 ? 's' : ''}) across {result.locations.length} location{result.locations.length !== 1 ? 's' : ''}.
          </div>
          {result.locations.map(loc => (
            <div key={loc.location_name} className="zl-result-loc">
              <div className="zl-result-loc-title">
                {loc.location_name} — zeroed {loc.zeroed_count} SKU{loc.zeroed_count !== 1 ? 's' : ''} ({loc.total_units} unit{loc.total_units !== 1 ? 's' : ''})
              </div>
              <a
                className="zl-btn-primary zl-download-link"
                href={`/api/zero-location/download/${loc.backup_file}`}
                download={loc.backup_file}
              >
                ⬇ Download Backup ({loc.backup_file})
              </a>
              {loc.drive_link && (
                <a
                  className="zl-btn-secondary zl-download-link"
                  href={loc.drive_link}
                  target="_blank"
                  rel="noreferrer"
                  style={{ marginLeft: 10 }}
                >
                  ☁ View in Google Drive
                </a>
              )}
              {loc.drive_error && (
                <div className="zl-banner zl-banner--warn" style={{ marginTop: 12 }}>
                  Backup was saved locally, but Drive upload failed: {loc.drive_error}
                </div>
              )}
              {loc.errors.length > 0 && (
                <div className="zl-banner zl-banner--warn" style={{ marginTop: 12 }}>
                  {loc.errors.length} SKU{loc.errors.length !== 1 ? 's' : ''} failed to zero:{' '}
                  {loc.errors.map(e => e.sku).join(', ')}
                </div>
              )}
            </div>
          ))}
          <div className="zl-actions" style={{ marginTop: 20 }}>
            <button className="zl-btn-secondary" onClick={reset}>Zero Out More Locations</button>
          </div>
        </div>
      )}

      {error && <div className="zl-error">{error}</div>}

      {backups.length > 0 && (
        <div className="zl-backups">
          <h2 className="zl-section-title">Previous Backups</h2>
          <div className="zl-backups-list">
            {backups.map(b => (
              <div key={b.name} className="zl-backup-item">
                <a href={`/api/zero-location/download/${b.name}`} download={b.name}>
                  {b.name} <span className="zl-backup-meta">{b.size_kb} KB</span>
                </a>
                {b.drive_link && (
                  <a href={b.drive_link} target="_blank" rel="noreferrer" className="zl-backup-drive-link">
                    ☁ Drive
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
